interface EdisonLogoProps {
  /** Width of the mark only (height scales 1:1) */
  size?: number;
  className?: string;
}

/** Brand blue tile. Map to your brand token if one exists. */
const EDISON_BLUE = "#0072D5";

/**
 * Edison icon mark — three bars on a transparent background.
 * Top and bottom bars are white; middle bar is gold (the "filament").
 * Wrap in a tile in the calling context, or use <EdisonTile />.
 *
 * Geometry (24u): bars h3 r1.5 at y4.5 / y10.5 / y16.5, x4.5,
 * arms 15u, middle 9.5u (63% — true E proportion).
 */
export function EdisonMark({ size = 18, className }: EdisonLogoProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <rect x="4.5" y="4.5" width="15" height="3" rx="1.5" fill="oklch(0.985 0.01 250)" />
      <rect x="4.5" y="10.5" width="9.5" height="3" rx="1.5" fill="oklch(0.80 0.14 80)" />
      <rect x="4.5" y="16.5" width="15" height="3" rx="1.5" fill="oklch(0.985 0.01 250)" />
    </svg>
  );
}

/** The mark inside its blue tile — use anywhere the mark stands alone. */
export function EdisonTile({ size = 32, className }: EdisonLogoProps) {
  return (
    <span
      className={className}
      style={{
        display: "grid",
        placeItems: "center",
        width: size,
        height: size,
        borderRadius: size * 0.229,
        background: EDISON_BLUE,
        flex: `0 0 ${size}px`,
      }}
    >
      <EdisonMark size={size} />
    </span>
  );
}

interface EdisonLogoLockupProps extends EdisonLogoProps {
  /** Show the "R&D INTELLIGENCE" descriptor beneath the wordmark */
  descriptor?: boolean;
  /** Wordmark colour */
  tone?: "light" | "dark";
}

/**
 * Full lockup: tile + live wordmark.
 * Text stays real text — selectable, accessible, and scales with the type system.
 * For exports where Geist is unavailable (PDF, slides, email, partners),
 * use /brand/edison-lockup.svg instead.
 */
export function EdisonLogo({
  size = 32,
  descriptor = false,
  tone = "dark",
  className,
}: EdisonLogoLockupProps) {
  return (
    <span
      className={className}
      style={{ display: "inline-flex", alignItems: "center", gap: size * 0.34 }}
    >
      <EdisonTile size={size} />
      <span style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
        <span
          style={{
            fontSize: size * 0.53,
            fontWeight: 600,
            letterSpacing: "-0.03em",
            color: tone === "light" ? "#FFFFFF" : "#15202B",
          }}
        >
          Edison
        </span>
        {descriptor && (
          <span
            style={{
              fontSize: size * 0.22,
              fontWeight: 600,
              letterSpacing: "0.11em",
              marginTop: size * 0.13,
              color: tone === "light" ? "rgba(255,255,255,0.55)" : "#74818F",
            }}
          >
            R&D INTELLIGENCE
          </span>
        )}
      </span>
    </span>
  );
}
